/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';

import { ThemeNames } from '@modernloop/shared/theme';
import { useFlag } from '@modernloop/shared/feature-flag';
import { GlobalStyles, ThemeProvider, useMediaQuery } from '@mui/material';

import {
  ColorScheme,
  ColorSchemePreference,
  ColorSchemePreferenceValue,
  SYSTEM_COLOR_SCHEME,
} from 'src/theme/ColorSchemePreference';
import createTheme from 'src/themeMui5';

type ColorSchemeContextValue = {
  colorScheme: ColorSchemePreferenceValue;
  isDarkMode: boolean;
  setColorScheme: (colorScheme: ColorSchemePreferenceValue) => void;
  toggleColorScheme: () => void;
};

const defaultContextValue: ColorSchemeContextValue = {
  colorScheme: ColorSchemePreference.DEFAULT,
  isDarkMode: false,
  setColorScheme: () => {},
  toggleColorScheme: () => {},
};

const ColorSchemeContext = createContext<ColorSchemeContextValue>(defaultContextValue);

type ColorSchemeGlobalStylesProps = {
  colorScheme: ColorScheme;
};

/** Applies native color-scheme hints for browser UI elements. */
const ColorSchemeGlobalStyles: FC<ColorSchemeGlobalStylesProps> = ({ colorScheme }) => (
  <GlobalStyles
    styles={{
      html: {
        colorScheme,
      },
    }}
  />
);

type ColorSchemeProviderProps = {
  children?: ReactNode;
};

/**
 * Provides app-wide MUI theming and persisted dashboard color scheme preference.
 */
export const ColorSchemeProvider: FC<ColorSchemeProviderProps> = ({ children }) => {
  const darkModeEnabled = useFlag('DARK_MODE');
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const [colorScheme, setColorSchemeState] = useState<ColorSchemePreferenceValue>(() =>
    ColorSchemePreference.fromStorage()
  );
  const selectedColorScheme = darkModeEnabled ? colorScheme : ColorSchemePreference.DEFAULT;
  const effectiveColorScheme: ColorScheme =
    selectedColorScheme === SYSTEM_COLOR_SCHEME
      ? systemPrefersDark
        ? ThemeNames.DARK
        : ThemeNames.LIGHT
      : selectedColorScheme;

  const setColorScheme = useCallback(
    (nextColorScheme: ColorSchemePreferenceValue) => {
      if (!darkModeEnabled) {
        return;
      }

      setColorSchemeState(nextColorScheme);
      ColorSchemePreference.persist(nextColorScheme);
    },
    [darkModeEnabled]
  );

  const toggleColorScheme = useCallback(() => {
    if (!darkModeEnabled) {
      return;
    }

    // The navbar toggle is an explicit override, so using it while following the system opts into light or dark.
    setColorScheme(effectiveColorScheme === ThemeNames.LIGHT ? ThemeNames.DARK : ThemeNames.LIGHT);
  }, [darkModeEnabled, effectiveColorScheme, setColorScheme]);

  const theme = useMemo(() => createTheme(effectiveColorScheme), [effectiveColorScheme]);

  const value = useMemo<ColorSchemeContextValue>(
    () => ({
      colorScheme: selectedColorScheme,
      isDarkMode: effectiveColorScheme === ThemeNames.DARK,
      setColorScheme,
      toggleColorScheme,
    }),
    [effectiveColorScheme, selectedColorScheme, setColorScheme, toggleColorScheme]
  );

  return (
    <ColorSchemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <ColorSchemeGlobalStyles colorScheme={effectiveColorScheme} />
        {children}
      </ThemeProvider>
    </ColorSchemeContext.Provider>
  );
};

/** Reads the persisted dashboard color scheme and theme controls. */
export const useColorScheme = (): ColorSchemeContextValue => useContext(ColorSchemeContext);
